// Lightweight GA4 wrapper, consent-gated.
// - Reads consent from localStorage 'cosec-consent-v1' (set by CookieBanner)
// - Only injects gtag.js after user grants analytics consent
// - send_page_view: false so we can manually track SPA route changes
// - Gracefully no-ops if REACT_APP_GA4_MEASUREMENT_ID is missing

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
const CONSENT_KEY = "cosec-consent-v1";

let isScriptLoaded = false;
let isInitialized = false;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  const c = readConsent();
  return !!(c && c.analytics);
}

function loadScript() {
  if (!MEASUREMENT_ID || isScriptLoaded || typeof document === "undefined") return;
  const existing = document.querySelector(`script[data-ga4-id="${MEASUREMENT_ID}"]`);
  if (existing) { isScriptLoaded = true; return; }
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  s.setAttribute("data-ga4-id", MEASUREMENT_ID);
  document.head.appendChild(s);
  isScriptLoaded = true;
}

function initGtag() {
  if (!MEASUREMENT_ID || isInitialized || typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    transport_type: "beacon",
    cookie_flags: "SameSite=Lax;Secure",
  });
  isInitialized = true;
}

export function enableAnalytics() {
  if (!MEASUREMENT_ID) return;
  loadScript();
  initGtag();
}

export function isAnalyticsEnabled() {
  return isInitialized && !!MEASUREMENT_ID;
}

export function trackPageView(path, title) {
  if (!isAnalyticsEnabled() || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    page_location: `${window.location.origin}${path}`,
    page_path: path,
  });
}

export function trackEvent(eventName, params) {
  if (!isAnalyticsEnabled() || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", eventName, params || {});
}

// Recommended event names, centralized to avoid typos.
export const GA_EVENTS = {
  CONTACT_SUBMIT: "contact_submit",
  BOOK_DEMO_CLICK: "book_demo_click",
  COVERIFY_DEMO_START: "coverify_demo_start",
  COVERIFY_DEMO_COMPLETE: "coverify_demo_complete",
  COSEARCH_QUERY: "cosearch_query",
  PRICING_VIEW: "pricing_view",
  SIGNUP_CLICKED: "signup_clicked",
  RELATED_ARTICLE_CLICK: "related_article_click",
  CS01_CALCULATOR_USED: "cs01_calculator_used",
};

// Auto-enable on first load if analytics consent already exists in localStorage
if (typeof window !== "undefined") {
  if (hasAnalyticsConsent()) {
    enableAnalytics();
  }
  // Listen for consent changes broadcast from the cookie banner
  window.addEventListener("cosec-consent", (e) => {
    if (e?.detail?.analytics) {
      enableAnalytics();
    }
  });
}
